import * as amplitudeAnalytics from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { SnackbarProvider, ThemeProvider } from "@getsoren/design-system";
import { GoogleTagManagerProvider, useGoogleTagManager } from "@getsoren/react-google-tag-manager";
import { useLocalStorage } from "@getsoren/react-utils";
import {
  ChatProvider,
  InitializeAmplitude,
  InitializeAxiosConfig,
  InitializeDaysJSConfig,
  InitializeHubSpot,
  InitializeI18nConfig,
  InitializeSentryConfig,
  InjectDependenciesProvider,
  QueryProvider,
  useCurrentLanguage,
} from "@getsoren/shared-module";
import * as sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import axios from "axios";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import tz from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import i18next from "i18next";
import languageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import * as reactRouter from "react-router";
import { SAAS_AXIOS_INSTANCE } from "@/api/customSaasInstance";
import AmplitudeUserSync from "@/components/Utils/AmplitudeUserSync";
import Router from "@/components/Utils/Router/Router";
import LOCAL_STORAGE_KEY from "@/constants/localStorageKey";
import TRANSLATIONS from "@/constants/translations";
import { version } from "~/package.json";

const libraries = {
  amplitude: {
    analytics: amplitudeAnalytics,
    sessionReplayPlugin,
  },
  axios,
  dayjs,
  gtm: {
    useGoogleTagManager,
  },
  i18: {
    i18next,
    initReactI18next,
    languageDetector,
  },
  reactQuery: {
    QueryClient,
    QueryClientProvider,
  },
  reactRouter,
  sentry,
};

const App = () => {
  const [isDarkTheme] = useLocalStorage(LOCAL_STORAGE_KEY.isDarkTheme, false);
  const language = useCurrentLanguage(i18next, "short");

  return (
    <InjectDependenciesProvider
      apiURL={import.meta.env.VITE_API_URL}
      libraries={libraries}
      localStorageKeys={{
        filter: LOCAL_STORAGE_KEY.filter,
        user: LOCAL_STORAGE_KEY.user,
      }}
    >
      <InitializeDaysJSConfig localizedFormat={localizedFormat} plugin={[relativeTime, utc, tz]} language={language} />
      <InitializeAxiosConfig axios={SAAS_AXIOS_INSTANCE} />
      <InitializeI18nConfig resources={TRANSLATIONS} />
      <InitializeSentryConfig
        dsn={import.meta.env.VITE_SENTRY_DSN}
        environment={import.meta.env.VITE_ENVIRONMENT || "no-env"}
        release={version}
      />
      <InitializeHubSpot hubId={import.meta.env.VITE_HUBSPOT_ID} />
      <InitializeAmplitude
        apiKey={import.meta.env.VITE_AMPLITUDE_API_KEY}
        appVersion={version}
        elementInteractions={{ pageUrlExcludelist: [/\/(?:login|forgot-password)(?:[?#]|$)/] }}
        platform="supplier"
        serverZone={import.meta.env.VITE_AMPLITUDE_SERVER_ZONE}
      />
      <GoogleTagManagerProvider id={import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID}>
        <QueryProvider>
          <ThemeProvider language={language} theme={isDarkTheme ? "dark" : "light"}>
            <SnackbarProvider>
              <ChatProvider context="supplier">
                <AmplitudeUserSync />
                <Router />
              </ChatProvider>
            </SnackbarProvider>
          </ThemeProvider>
        </QueryProvider>
      </GoogleTagManagerProvider>
    </InjectDependenciesProvider>
  );
};

export default App;
